import { CustomCombobox, DatePickerWithRange, RangeInputField } from "@/components/shared";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GetPaginatedTendersQueryVariables, TenderState, FetchUsersDocument, PaginatedOrganizationsDocument, FetchUsersQuery, FetchUsersQueryVariables, PaginatedOrganizationsQuery, PaginatedOrganizationsQueryVariables } from "@/graphql/__generated__/graphql";
import { useQuery } from "@apollo/client/react";
import { updatePaginationState, usePaginationState } from "@/store/paginationStore";
import useUserStore from "@/store/shield";
import { getUserFriendlyTenderState, convertDecimalToBp, convertBpToDecimalOrUndefined, handleTenderStateColour, sortOptionsByLabel } from "@/utils/helpers";
import { FunnelIcon } from "@heroicons/react/24/outline";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { JSX, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import ArchivedToggleFilter from "./ArchivedToggleFilter";
import { Separator } from "@/components/ui/separator";
import { useBreakpoint } from "@/hooks";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type RadioOption = {
  label: string;
  value: TenderState;
}

type FilterState = {
  archived: boolean | null;
  operationalOrgs: number[];
  chanceAdmins: number[];
  state: TenderState[];
  search: string;
  startDate: { gte: Date | undefined; lte: Date | undefined };
  deadlineDate: { gte: Date | undefined; lte: Date | undefined };
  revenue: { gte: number | undefined; lte: number | undefined };
  surcharge: { gte: number | undefined; lte: number | undefined };
};

interface Props {
  displayAmount: number;
  handleResetClickFilters: (_preserveSort?: boolean) => void;
  handleSubmitClick: (_variables?: GetPaginatedTendersQueryVariables) => void;
}

/**
 * TenderFiltersDialog component
 * Provides a dialog interface for filtering tenders with various criteria
 *
 * @param {Props} props - Component props
 * @param {number} props.displayAmount - Number of items to display per page
 * @param {Function} props.handleResetClickFilters - Function to reset all filters
 * @param {Function} props.handleSubmitClick - Function to handle submitting updated query variables
 * @returns {JSX.Element} The tender filters dialog component
 */
const TenderFiltersDialog = (props: Props): JSX.Element => {

  const { handleResetClickFilters, handleSubmitClick, displayAmount } = props;

  // State
  const [open, setOpen] = useState<boolean>(false);

  // Internal component filter state
  const [filters, setFilters] = useState<FilterState>({
    archived: null,
    operationalOrgs: [],
    chanceAdmins: [],
    state: [],
    search: "",
    startDate: { gte: undefined, lte: undefined },
    deadlineDate: { gte: undefined, lte: undefined },
    revenue: { gte: undefined, lte: undefined },
    surcharge: { gte: undefined, lte: undefined }
  });

  // Hooks
  const { t } = useTranslation(["translation", "common", "components"]);
  const isLgBreakpoint = useBreakpoint("lg");
  const currentUserId = useUserStore((state) => state.userId);

  // Fetch users for the chance admin filter
  const { data: usersData, loading: usersLoading } = useQuery<FetchUsersQuery, FetchUsersQueryVariables>(FetchUsersDocument, {
    variables: { first: 100 }
  });

  // Fetch operational organizations for the org filter
  const { data: operationalOrgsData, loading: operationalOrgsLoading } = useQuery<PaginatedOrganizationsQuery, PaginatedOrganizationsQueryVariables>(PaginatedOrganizationsDocument, {
    variables: {
      first: 100,
      filters: {
        isOperational: true
      }
    }
  });

  // Extract data from query results
  const usersArr = usersData?.fetchUsers || [];
  const operationalOrgsArr = operationalOrgsData?.paginatedOrganizations.edges || [];

  // Sort users options by name
  const usersOptions = useMemo(() => {
    const options = usersArr.map((user) => {
      const fullName = `${user.firstname} ${user.lastname}`;

      return {
        label: fullName,
        value: user.id,
        searchValue: fullName, // Enable searching by full name
      };
    });

    const currentUserOption =
      currentUserId !== 0 ? options.find((option) => option.value === currentUserId) : undefined;
    const remainingOptions =
      currentUserId !== 0 ? options.filter((option) => option.value !== currentUserId) : options;

    return [
      ...(currentUserOption ? [currentUserOption] : []),
      ...sortOptionsByLabel(remainingOptions),
    ];
  }, [currentUserId, usersArr]);

  // Sort operational organizations options by name
  const operationalOrgOptions = useMemo(() => {
    const options = operationalOrgsArr.map((option) => ({
      label: option.node.shortName,
      value: option.node.id,
      searchValue: option.node.shortName,
    }));

    return sortOptionsByLabel(options);
  }, [operationalOrgsArr]);

  // Get current filter state from pagination store
  const paginationState: PaginationState["paginatedTenders"] = usePaginationState("paginatedTenders");
  const { filters: {
    archived: archivedFromStore,
    startDate,
    deadlineDate,
    state,
    search,
    operationalOrgs,
    chanceAdmins,
    revenue,
    surcharge,
  } } = paginationState as any;

  /**
   * Handles toggling a chance admin filter value
   * @param {number} value - The chance admin ID to toggle in the filter
   * @description
   * This function adds or removes a chance admin from the filter.
   * Updates the pagination state without auto-submitting.
   */
  const handleToggleChanceAdmin = (value: number[] | null) => {
    setFilters(prev => ({ ...prev, chanceAdmins: value ?? [] }));
  };

  // Define available tender state options for the filter
  const stateFilters: RadioOption[] = [
    { label: getUserFriendlyTenderState(t, TenderState.PendingBidDecision), value: TenderState.PendingBidDecision },
    { label: getUserFriendlyTenderState(t, TenderState.Editing), value: TenderState.Editing },
    { label: getUserFriendlyTenderState(t, TenderState.PendingMarginDecision), value: TenderState.PendingMarginDecision },
    { label: getUserFriendlyTenderState(t, TenderState.ReadyToSubmit), value: TenderState.ReadyToSubmit },
    { label: getUserFriendlyTenderState(t, TenderState.Submitted), value: TenderState.Submitted },
    { label: getUserFriendlyTenderState(t, TenderState.Won), value: TenderState.Won },
    { label: getUserFriendlyTenderState(t, TenderState.Lost), value: TenderState.Lost },
    { label: getUserFriendlyTenderState(t, TenderState.NotBidding), value: TenderState.NotBidding },
    { label: getUserFriendlyTenderState(t, TenderState.Annulment), value: TenderState.Annulment },
  ];

  /**
   * Handles toggling a tender state filter value
   * @param {TenderState} value - The tender state value to toggle
   * @description
   * Adds or removes a tender state from the filter.
   * Updates the state in the pagination store without auto-submitting.
   */
  const handleToggleState = (value: TenderState) => {
    setFilters(prev => ({
      ...prev,
      state: prev.state.includes(value)
        ? prev.state.filter(item => item !== value)
        : [...prev.state, value]
    }));
  };

  /**
   * Handles toggling an operational organization filter value
   * @param {number} value - The operational org ID to toggle
   * @description
   * Adds or removes an operational org from the filter.
   * Updates the pagination state without auto-submitting.
   */
  const handleToggleOperationalOrg = (value: number[] | null) => {
    setFilters(prev => ({ ...prev, operationalOrgs: value ?? [] }));
  };

  /**
   * Updates the revenue range filter
   * @param {("lte"|"gte")} type - The type of range limit to update (less than/equal or greater than/equal)
   * @param {string|number} value - The new value for the range limit
   */
  const handleRevenueChange = (type: "lte" | "gte", value: string | number) => {
    setFilters(prev => ({
      ...prev,
      revenue: {
        ...prev.revenue,
        [type]: value != null ? parseFloat(value.toString()) : null
      }
    }));
  };

  /**
   * Updates the surcharge range filter
   * @param {("lte"|"gte")} type - The type of range limit to update (less than/equal or greater than/equal)
   * @param {string|number} value - The new value for the range limit
   */
  const handleSurchargeChange = (type: "lte" | "gte", value: string | number) => {
    setFilters(prev => ({
      ...prev,
      surcharge: {
        ...prev.surcharge,
        [type]: value != null ? parseFloat(value.toString()) : null
      }
    }));
  };

  /**
   * Handles keyboard events on the input field
   * @param {React.KeyboardEvent<HTMLInputElement>} event - Keyboard event
   * @description
   * Submits the filters when Enter is pressed
   * Resets filters when Escape is pressed
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleConfirmClick();
    }
    if (event.key === "Escape") {
      handleResetClickFilters();
    }
  };

  /**
   * Updates the free-text search filter
   * @param {string} value - The new search filter value
   */
  const onChangeTextInput = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
  };

  /**
   * Updates the start date range filter
   * @param {{ from: Date | undefined; to: Date | undefined }} newDateRange - The new date range
   */
  function setDateRange(newDateRange: { from: Date | undefined; to: Date | undefined }) {
    setFilters(prev => ({
      ...prev,
      startDate: { gte: newDateRange.from, lte: newDateRange.to }
    }));
  }

  /**
   * Updates the deadline date range filter
   * @param {{ from: Date | undefined; to: Date | undefined }} newDateRange - The new date range
   */
  function setDeadlineDateRangeHandler(newDateRange: { from: Date | undefined; to: Date | undefined }) {
    setFilters(prev => ({
      ...prev,
      deadlineDate: { gte: newDateRange.from, lte: newDateRange.to }
    }));
  }

  /**
   * Handles the confirm button click
   * @description
   * Updates the pagination state with the new filters
   * Submits the filters to the server
   * Closes the dialog
   */
  function handleConfirmClick() {
    const filterValues = {
      archived: filters.archived,
      operationalOrgs: filters.operationalOrgs.length === 0 ? null : filters.operationalOrgs,
      chanceAdmins: filters.chanceAdmins.length === 0 ? null : filters.chanceAdmins,
      state: filters.state.length === 0 ? null : filters.state,
      search: filters.search || null,
      startDate: {
        gte: filters.startDate?.gte ? format(filters.startDate.gte, "yyyy-MM-dd") + "T00:00:00+00:00" : null,
        lte: filters.startDate?.lte ? format(filters.startDate.lte, "yyyy-MM-dd") + "T23:59:59+00:00" : null
      },
      deadlineDate: {
        gte: filters.deadlineDate?.gte ? format(filters.deadlineDate.gte, "yyyy-MM-dd") + "T00:00:00+00:00" : null,
        lte: filters.deadlineDate?.lte ? format(filters.deadlineDate.lte, "yyyy-MM-dd") + "T23:59:59+00:00" : null
      },
      revenue: {
        gte: filters.revenue?.gte != null ? filters.revenue.gte : null,
        lte: filters.revenue?.lte != null ? filters.revenue.lte : null
      },
      surcharge: {
        gte: filters.surcharge?.gte != null ? convertDecimalToBp(filters.surcharge.gte) : null,
        lte: filters.surcharge?.lte != null ? convertDecimalToBp(filters.surcharge.lte) : null
      }
    };

    updatePaginationState({
      paginatedTenders: {
        filters: filterValues
      }
    });

    handleSubmitClick({
      first: displayAmount,
      filters: filterValues
    });

    setOpen(false);
  }

  /**
   * Handles the reset button click
   * @description
   * Resets the filters to the default values
   * Updates the pagination state
   * Submits the filters to the server
   * Closes the dialog
   */
  const handleResetClick = () => {
    setFilters({
      archived: null,
      operationalOrgs: [],
      chanceAdmins: [],
      state: [],
      search: "",
      startDate: { gte: undefined, lte: undefined },
      deadlineDate: { gte: undefined, lte: undefined },
      revenue: { gte: undefined, lte: undefined },
      surcharge: { gte: undefined, lte: undefined }
    });
    handleResetClickFilters(true);
  };

  // Update filters when the pagination state changes
  // to persist global filters values
  useEffect(() => {
    setFilters({
      archived: archivedFromStore ?? null,
      operationalOrgs: operationalOrgs ?? [],
      chanceAdmins: chanceAdmins ?? [],
      state: state ?? [],
      search: search ?? "",
      startDate: { gte: startDate?.gte ?? undefined, lte: startDate?.lte ?? undefined },
      deadlineDate: { gte: deadlineDate?.gte ?? undefined, lte: deadlineDate?.lte ?? undefined },
      revenue: { gte: revenue?.gte ?? undefined, lte: revenue?.lte ?? undefined },
      surcharge: {
        gte: convertBpToDecimalOrUndefined(surcharge?.gte),
        lte: convertBpToDecimalOrUndefined(surcharge?.lte)
      }
    });
  }, [archivedFromStore, operationalOrgs, chanceAdmins, state, search, startDate, deadlineDate, revenue, surcharge, open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <FunnelIcon className="icon-sm" />
          {t("common:buttons.filters")}
        </Button>
      </DialogTrigger>
      <DialogContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => {
          // Prevent auto-focus on mobile devices to avoid opening the keyboard
          if (!isLgBreakpoint) {
            e.preventDefault();
          }
        }}
        className="w-auto max-w-[90vw] lg:max-w-fit overflow-y-auto max-h-[90vh] h-auto"
      >
        <DialogHeader>
          <DialogTitle>{t("common:buttons.filters")}</DialogTitle>
          <DialogDescription className="sr-only">{t("common:buttons.filters")}</DialogDescription>
        </DialogHeader>
        {/* Top container --> Input and Action buttons */}
        <div className="flex items-center w-full">
          {/* Left container --> Input */}
          <div className="flex-1 grow">
            <Input
              value={filters.search}
              type="text"
              onChange={(event) => onChangeTextInput(event.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus={isLgBreakpoint}
              placeholder={t("components:filters.tenders.searchplaceholder")}
            />
          </div>
        </div>

        {/* State filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild className="w-full">
            <Button variant="outline" className="w-full h-full items-center flex">
              {t("components:filters.tenders.chancestate")} {filters.state && filters.state?.length > 0 ? `: ${filters.state?.length}` : ""}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-full">
            {stateFilters.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={filters.state?.includes(option.value)}
                onCheckedChange={() => handleToggleState(option.value)}
                onSelect={(event) => event.preventDefault()}
                className="flex gap-2"
              >
                <Badge variant="outline" className={cn("text-xs py-0 h-5 text-white border-0", handleTenderStateColour(option.value, true, true, false))}>
                  {option.label}
                </Badge>
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Bottom container --> Date, State, Admin, Operational Orgs, Margin and Revenue Filters */}
        <div className="flex items-center gap-2">
          {/* DatePicker - Start Date */}
          <DatePickerWithRange
            data-cy="date-picker"
            filterText={t("common:startdate")}
            dateRange={{
              from: filters.startDate?.gte || undefined,
              to: filters.startDate?.lte || undefined,
            }}
            setDateRange={setDateRange}
            className="w-full"
            endMonth={new Date(new Date().setFullYear(new Date().getFullYear() + 5))}
          />
          {/* DatePicker - Deadline */}
          <DatePickerWithRange
            data-cy="date-picker"
            filterText={t("common:deadline")}
            dateRange={{
              from: filters.deadlineDate?.gte || undefined,
              to: filters.deadlineDate?.lte || undefined,
            }}
            setDateRange={setDeadlineDateRangeHandler}
            className="w-full"
            endMonth={new Date(new Date().setFullYear(new Date().getFullYear() + 5))}
          />
        </div>

        <div className="flex items-center gap-2 -mb-2">
          {/* Chance admin/owner filter */}
          <CustomCombobox
            multiple
            triggerClassName="w-[49%]"
            loading={usersLoading}
            options={usersOptions}
            value={filters.chanceAdmins}
            onChange={(value) => {
              handleToggleChanceAdmin(value as number[] | null);
            }}
            placeholder={t("components:filters.tenders.chanceadmins")}
            formatSelectedLabel={(selected) =>
              `${t("components:filters.tenders.chanceadmins")}${selected.length > 0 ? `: ${selected.length}` : ""}`
            }
            popoverProps={{
              onWheel: (e) => {
                e.stopPropagation();
              }
            }}
          />

          {/* Operation orgs filter */}
          <CustomCombobox
            multiple
            triggerClassName="w-[49%]"
            loading={operationalOrgsLoading}
            options={operationalOrgOptions}
            value={filters.operationalOrgs}
            onChange={(value) => {
              handleToggleOperationalOrg(value as number[] | null);
            }}
            placeholder={t("components:filters.tenders.operationalorgs")}
            formatSelectedLabel={(selected) =>
              `${t("components:filters.tenders.operationalorgs")}${selected.length > 0 ? `: ${selected.length}` : ""}`
            }
            popoverProps={{
              onWheel: (e) => {
                e.stopPropagation();
              }
            }}
          />
        </div>

        {/* Revenue Range Filter */}
        <RangeInputField
          className="w-auto"
          label={t("components:filters.tenders.revenue")}
          fromValue={filters.revenue?.gte ?? ""}
          toValue={filters.revenue?.lte ?? ""}
          onFromChange={(value) => handleRevenueChange("gte", value)}
          onToChange={(value) => handleRevenueChange("lte", value)}
          useFormattedInput={true}
          style="currency"
          currency="EUR"
        />

        {/* Surcharge Range Filter */}
        <RangeInputField
          className="w-auto"
          label={t("components:filters.tenders.surcharge")}
          fromValue={filters.surcharge?.gte ?? ""}
          toValue={filters.surcharge?.lte ?? ""}
          onFromChange={(value) => handleSurchargeChange("gte", value)}
          onToChange={(value) => handleSurchargeChange("lte", value)}
          useFormattedInput={true}
          style="decimal"
          additionalSuffix="%"
        />

        <Separator />

        <ArchivedToggleFilter
          archived={filters.archived}
          onArchivedChange={(value) => setFilters(prev => ({ ...prev, archived: value }))}
        />

        {/* Footer container --> Cancel and Confirm buttons */}
        <DialogFooter>
          <div className="flex-1">
            <Button variant="destructive" onClick={handleResetClick}>
              {t("common:buttons.reset")}
            </Button>
          </div>
          <DialogClose asChild>
            <Button variant="outline">
              {t("common:buttons.close")}
            </Button>
          </DialogClose>
          <Button variant="default" onClick={() => handleConfirmClick()}>
            {t("common:buttons.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TenderFiltersDialog;
